import { trackProductTileBrandLinkHoverTiming } from "@faire/web-api/events/productTile/timing/brandLinkHover";
import { trackSeoSearchProductListBrandLinkRefsClick } from "@faire/web-api/events/seo/click/searchProductListBrandLinkRefs";
import { TimingMarker, TimingPair } from "@faire/web/common/timings/TimingMarker";
import { trackEvent } from "@faire/web/common/trackEvent";
import kebabCase from "lodash/kebabCase";
import React, { useCallback, useContext, useMemo, useState } from "react";



import { handleGenerateBrandUrl } from "@faire/retailer/components/SearchProducts/utils";
import { ProductTileContext } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/common";
import { BrandInfoProps } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/BrandInfo/brandInfoProps";
import { useGetProductTileClickImpressions } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/productTileActions/useGetProductTileClickImpressions";
import { ProductClickForSearchPageParameters } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/productTileActions/useOnProductClickForSearchPage";
import { useSearchPageProductSearchString } from "@faire/retailer/views/search-v2/SearchPageQueryParams/useSearchPageProductSearchString";



import { ProductTileBrandInfoClickContext } from "./ProductTileBrandInfoClickContext";


/**
 * Used for brand info on search page (both logged in and logged out)
 */
export const SearchProductTileBrandInfoClickContextProvider: React.FC<{
  data: BrandInfoProps;
  children: React.ReactNode;
}> = ({ data, children }) => {
  const [hoverStartTime, setHoverStartTime] = useState<TimingMarker | null>(
    null
  );
  const trackingParams: ProductClickForSearchPageParameters = {
    token: data.token,
    isPromoted: data.isPromoted,
    impressionId: data.impressionId,
    promotedBy: data.promotedBy,
    badgeList: data.badgeList,
    bestImage: data.bestImage,
    images: data.images,
  };
  const { brandToken } = useContext(ProductTileContext);
  const onBrandClickSearchPage = useOnBrandClickSearchPage(trackingParams);
  const onBrandClick = useCallback(
    (ev: React.MouseEvent<HTMLAnchorElement>) => {
      onBrandClickSearchPage.onBrandClick(ev);
      const endTime = new TimingMarker();
      const timer: TimingPair = {
        start: hoverStartTime!,
        end: endTime,
      };
      trackProductTileBrandLinkHoverTiming("click", timer);
      setHoverStartTime(null);
    },
    [onBrandClickSearchPage, hoverStartTime]
  );
  const onBrandLinkFocus = useCallback(() => {
    if (!hoverStartTime) {
      setHoverStartTime(new TimingMarker());
    }
  }, [hoverStartTime]);
  const onBrandLinkBlur = useCallback(() => {
    const endTime = new TimingMarker();
    const timer: TimingPair = {
      start: hoverStartTime!,
      end: endTime,
    };
    trackProductTileBrandLinkHoverTiming("hover", timer);
  }, [hoverStartTime]);

  const queryString = useSearchPageProductSearchString();

  const generateBrandUrl = useMemo(
    () =>
      handleGenerateBrandUrl(queryString, {
        productToken: trackingParams.token,
        brandToken,
        impressionId: trackingParams.impressionId,
      }),
    [queryString, trackingParams.token, trackingParams.impressionId, brandToken]
  );
  return (
    <ProductTileBrandInfoClickContext.Provider
      value={{
        onBrandClick,
        generateBrandUrl,
        onBrandLinkFocus,
        onBrandLinkBlur,
      }}
    >
      {children}
    </ProductTileBrandInfoClickContext.Provider>
  );
};

export const useOnBrandClickSearchPage = (
  params: ProductClickForSearchPageParameters
) => {
  const { recordBrandNameClickEvent } =
    useGetProductTileClickImpressions(params);
  const queryString = useSearchPageProductSearchString();
  const { brandToken } = useContext(ProductTileContext);

  return {
    onBrandClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
      recordBrandNameClickEvent();

      const productToken = params.token;
      if (!queryString || !productToken || !brandToken) {
        return;
      }
      trackEvent(
        `/search-click/${kebabCase(
          queryString
        )}/product/${productToken}/brand/${brandToken}`
      );

      trackSeoSearchProductListBrandLinkRefsClick(
        brandToken ?? "",
        queryString
      );

      event.stopPropagation();
    },
  };
};
